"use client";

import { SABINE_NAME } from "@/lib/sabine";
import { SABINE_SOLID } from "@/components/nav/nav-styles";
import { renderAIChatMarkdown } from "@/lib/ai-chat-markdown";
import AIChatFeedbackButtons from "@/components/AIChatFeedbackButtons";
import AIChatCopyButton from "@/components/AIChatCopyButton";
import AgentActionLinks from "@/components/agent-actions/AgentActionLinks";
import { cn } from "@/lib/utils";
import { describeAssistantTurn } from "@/lib/ai-chat-feedback-utils";

// The thinking dots pulse in turn; full class names so Tailwind sees them.
const THINKING_DOT_DELAYS = [
  "[animation-delay:0s]",
  "[animation-delay:0.15s]",
  "[animation-delay:0.3s]",
];

function SabineLabel() {
  return <p className="text-xs font-semibold text-slate-400">{SABINE_NAME}</p>;
}

/**
 * One row of Sabine's thread: what the rep said, or Sabine's answer with its
 * thumbs and its copy button. The thumbs vote on the row the chat's route already recorded
 * for this turn (`describeAssistantTurn`); `deferFeedback` holds them back
 * while the answer is still streaming.
 *
 * @param {object} props
 * @param {object} props.message
 * @param {number} props.index Where the message sits in `messages`
 * @param {Array} props.messages The thread, to find the question an answer replies to
 * @param {string} props.relativeTime "2m ago", or "" before the clock starts
 * @param {boolean} props.deferFeedback
 * @param {string} props.source The chat's AIChatFeedbackSource
 * @param {object} props.feedbackContext What a vote is filed with (chatFeedbackContext)
 * @param {boolean} props.showFeedback False when the caller renders its own thumbs
 * @param {boolean} props.showCopyAction
 * @param {Function} [props.formatCopyText] What the copy button copies, from the answer
 * @param {Function} [props.renderMessageContent] A caller's own answer body
 */
export default function AIChatMessage({
  message,
  index,
  messages,
  relativeTime,
  deferFeedback,
  source,
  feedbackContext,
  showFeedback,
  showCopyAction,
  formatCopyText,
  renderMessageContent,
}) {
  if (message.role !== "assistant") {
    return (
      <div className="flex flex-col items-end gap-1 animate-message-in">
        <div
          data-testid="ai-chat-message-user"
          className={cn(
            "max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-2.5",
            SABINE_SOLID,
          )}
        >
          <div className="whitespace-pre-wrap text-sm leading-relaxed">
            {message.content}
          </div>
        </div>
        {relativeTime ? (
          <span className="px-1 text-[11px] text-slate-400">{relativeTime}</span>
        ) : null}
      </div>
    );
  }

  const turn = describeAssistantTurn(messages, index, source);
  const copyText = formatCopyText ? formatCopyText(turn.answer) : turn.answer;
  const content = renderMessageContent ? (
    renderMessageContent(message, { isAssistant: true, index })
  ) : (
    <div className="flex-1 min-w-0 text-sm text-slate-800">
      <div
        className="prose prose-sm max-w-none text-slate-800 overflow-x-auto"
        dangerouslySetInnerHTML={{
          __html: renderAIChatMarkdown(message.content || ""),
        }}
      />
      {/* Where the answer sends you, as a button rather than a link to find
          mid-paragraph — the affordance the home hero has always had. A caller
          that renders its own message body owns its own action row (the
          reports chat shows model-verified destinations instead), so this is
          the default path only. */}
      <AgentActionLinks content={message.content || ""} className="mt-3" />
    </div>
  );

  return (
    <div
      data-testid="ai-chat-message-assistant"
      className="space-y-1.5 animate-message-in"
    >
      <SabineLabel />
      <div className="min-w-0 space-y-3">
        {content}
        <div className="flex items-center">
          {showFeedback && !deferFeedback && turn.question && turn.answer ? (
            <AIChatFeedbackButtons
              question={turn.question}
              answer={turn.answer}
              source={source}
              contextId={feedbackContext?.contextId}
              contextType={feedbackContext?.contextType}
              workspaceId={feedbackContext?.workspaceId}
              messageId={turn.feedbackMessageId}
              responseTimeMs={message.responseTimeMs}
              trailingAction={
                showCopyAction ? <AIChatCopyButton text={copyText} /> : null
              }
              metadata={{
                ...feedbackContext,
                assistantMessageId: turn.assistantMessageId,
                userMessageId: turn.userMessageId,
              }}
            />
          ) : showCopyAction && turn.answer && !deferFeedback ? (
            <AIChatCopyButton text={copyText} />
          ) : null}
        </div>
        {relativeTime ? (
          <span className="block text-[11px] text-slate-400">{relativeTime}</span>
        ) : null}
      </div>
    </div>
  );
}

/** The row shown while Sabine is answering: her label and three pulsing dots, or the caller's own content. */
export function AIChatThinking({ renderContent }) {
  return (
    <div data-testid="ai-chat-processing" className="space-y-1.5 animate-message-in">
      <SabineLabel />
      <div className="min-w-0">
        {renderContent ? (
          renderContent()
        ) : (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="flex items-center gap-1" aria-hidden>
              {THINKING_DOT_DELAYS.map((delay) => (
                <span
                  key={delay}
                  className={cn(
                    "thinking-dot h-1.5 w-1.5 rounded-full",
                    SABINE_SOLID,
                    delay,
                  )}
                />
              ))}
            </span>
            {SABINE_NAME} is thinking…
          </div>
        )}
      </div>
    </div>
  );
}
